import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { VisionBoard } from './VisionBoard';
import { Modal } from './Modal';
import { useAuth } from '../contexts/AuthContext';
import { visionBoardService } from '../services/visionBoard.service';
import LoadingSpinner from './LoadingSpinner';
import styled from 'styled-components';
import { colors } from '../styles';
import { useToast } from '../contexts/ToastContext';

const UserBoard = () => {
  const { username } = useParams();
  const { userProfile } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(null);
  const [owner, setOwner] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const isOwner = userProfile?.username === username;

  const loadBoard = useCallback(async () => {
    try {
      const userData = await visionBoardService.getUserByUsername(username);
      setOwner(userData);
      
      const boardData = await visionBoardService.getUserBoard(userData.uid, isOwner);
      setBoard(boardData);
    } catch (error) {
      console.error('Error completo:', error);
      toast.showToast('Usuario no encontrado', 'error');
      setBoard([]);
    } finally {
      setLoading(false);
    }
  }, [username, isOwner, toast]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedCard(null);
    setModalOpen(false);
  };

  const handleDeleteCard = async (cardId) => {
    try {
      if (!cardId) return;
      await visionBoardService.deleteCard(cardId);
      setBoard(prev => prev.filter(card => card.docId !== cardId));
      toast.showToast('Tarjeta eliminada correctamente', 'success');
      handleCloseModal();
    } catch (error) {
      console.error('Error al eliminar la tarjeta:', error);
      toast.showToast('Error al eliminar la tarjeta', 'error');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!board || board.length === 0) {
    return (
      <ErrorContainer>
        <h2>{owner ? 'Board vacío' : 'Board no encontrado'}</h2>
        <p>
          {owner 
            ? 'Este usuario aún no ha agregado ninguna tarjeta pública'
            : `El usuario ${username} no existe`}
        </p>
      </ErrorContainer>
    );
  }

  return (
    <>
      <Container>
        <BoardHeader>
          <OwnerInfo>
            <Avatar 
              src={owner.photoURL} 
              alt={owner.displayName}
              onError={(e) => {
                e.target.src = 'https://api.dicebear.com/7.x/avataaars/svg?seed=default';
              }}
            />
            <div>
              <OwnerName>{owner.displayName || 'Usuario'}</OwnerName>
              <Username>@{username}</Username>
            </div>
          </OwnerInfo>
        </BoardHeader>
        <VisionBoard
          board={board}
          isOwner={isOwner}
          onCardClick={handleCardClick}
          onDeleteCard={isOwner ? handleDeleteCard : null}
        />
        {modalOpen && selectedCard && (
          <Modal 
            card={selectedCard} 
            closeModal={handleCloseModal}
            onDelete={isOwner ? () => handleDeleteCard(selectedCard.docId) : null}
            isOwner={isOwner}
          />
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 2rem;
  background-color: ${colors.primary};
  min-height: 100vh;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${colors.primary};
  color: ${colors.text};
  text-align: center;
  padding: 2rem;

  h2 {
    font-family: 'Caveat', cursive;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  p {
    font-family: 'Indie Flower', cursive;
    font-size: 1.2rem;
  }
`;

const BoardHeader = styled.div`
  background-color: ${colors.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px ${colors.shadow};
  margin-bottom: 2rem;
`;

const OwnerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const Avatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${colors.secondary};
`;

const OwnerName = styled.h2`
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  color: ${colors.text};
  margin: 0;
`;

const Username = styled.p`
  font-family: 'Indie Flower', cursive;
  font-size: 1.2rem;
  color: ${colors.accent};
  margin: 0;
`;

export default UserBoard; 